import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import styled from 'styled-components';
import { Card, CardBody, CardSubhead, CardTitle } from '@spark/ui-components/components/Card';
import JanelaDescargaHint, { CardTitleRow } from './JanelaDescargaHint';
import { getLojaLabel } from '../utils/lojaLabel';

const formatSegundosParaHhMmSs = (totalSegundos) => {
  const segundos = Math.max(0, Math.floor(Number(totalSegundos) || 0));
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const restoSegundos = segundos % 60;

  return [
    String(horas).padStart(2, '0'),
    String(minutos).padStart(2, '0'),
    String(restoSegundos).padStart(2, '0')
  ].join(':');
};

const RankingTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload || {};
  const lojaLabel = getLojaLabel(item.loja);
  const tempo = item.tempoMedioDescarga || formatSegundosParaHhMmSs(item.tempoMedioSegundos);

  return (
    <TooltipBox>
      <strong>{lojaLabel}</strong>
      <div>Tempo medio: {tempo}</div>
      <div>Cargas: {item.quantidadeCargas ?? 0}</div>
    </TooltipBox>
  );
};

const RankingMediaTempoLojaCard = ({
  data,
  loading
}) => {
  const chartData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  return (
    <Card>
      <CardBody>
        <CardTitleRow>
          <CardTitle>Ranking tempo medio de descarga por loja</CardTitle>
          <JanelaDescargaHint />
        </CardTitleRow>
        <CardSubhead>Top 10 lojas por media de tempo de descarga</CardSubhead>

        <ChartArea>
          {loading ? (
            <EmptyState>Carregando metricas...</EmptyState>
          ) : chartData.length === 0 ? (
            <EmptyState>Nenhum registro encontrado.</EmptyState>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="loja" tickLine={false} axisLine={false} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={formatSegundosParaHhMmSs}
                  width={72}
                />
                <Tooltip content={<RankingTooltip />} />
                <Bar
                  dataKey="tempoMedioSegundos"
                  name="Tempo medio de descarga"
                  fill="#5b8def"
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartArea>

        <LegendFooter>
          <LegendItem>
            <LegendDot color="#5b8def" />
            <span>Tempo medio de descarga</span>
          </LegendItem>
        </LegendFooter>
      </CardBody>
    </Card>
  );
};

const ChartArea = styled.div`
  height: 270px;
  margin-top: 18px;
`;

const LegendFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 18px;
  margin: 18px -20px -20px;
  padding: 12px 20px 18px;
  border-top: 1px solid #d8dde3;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
`;

const LegendDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ color }) => color};
  display: inline-block;
  flex: 0 0 14px;
`;

const EmptyState = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  background: #f8f9fa;
  border: 1px dashed #d8dde3;
`;

const TooltipBox = styled.div`
  background: #fff;
  border: 1px solid #d8dde3;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 12px;
  color: #374151;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  strong {
    display: block;
    margin-bottom: 4px;
  }
`;

export default RankingMediaTempoLojaCard;
